import * as React from 'react';
import {
    AppBar, Box, Drawer, IconButton, List, ListItem,
    ListItemButton, ListItemIcon, ListItemText, Toolbar,
    Typography, Divider, Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { getBaseURLWithAuth } from '../common/utils/session';
import UserIcon from './user-icon';
import MenuGroups from './menu-itens';
import Logo from '../assets/images/logo.svg';
import { useThemeMode } from '../context/theme-context';

const DRAWER_WIDTH = 240;

function SidebarContent({ onNavigate }: { onNavigate: (url: string) => void }) {
    const location = useLocation();

    const isActive = (url: string) =>
        location.pathname === getBaseURLWithAuth() + url ||
        location.pathname.startsWith(getBaseURLWithAuth() + url + '/');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Logo header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2.5 }}>
                <img src={Logo} width={32} height={32} alt="logo" />
                <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: 'primary.main', letterSpacing: '0.04em', fontSize: '1rem' }}
                >
                    CryptoPortfolio
                </Typography>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,215,0,0.12)' }} />

            {/* Navigation */}
            <Box sx={{ flex: 1, overflowY: 'auto', pt: 1 }}>
                {MenuGroups.map((group, gi) => (
                    <Box key={group.label}>
                        <Typography
                            variant="caption"
                            sx={{
                                px: 2.5,
                                pt: gi === 0 ? 1 : 1.5,
                                pb: 0.5,
                                display: 'block',
                                color: 'text.secondary',
                                fontWeight: 600,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                fontSize: '0.67rem',
                            }}
                        >
                            {group.label}
                        </Typography>
                        <List disablePadding>
                            {group.items.map((item) => {
                                const active = isActive(item.url);
                                const Icon = item.icon;
                                return (
                                    <ListItem key={item.id} disablePadding sx={{ px: 1 }}>
                                        <ListItemButton
                                            onClick={() => onNavigate(item.url)}
                                            sx={{
                                                borderRadius: 1.5,
                                                mb: 0.25,
                                                minHeight: 44,
                                                pl: active ? 'calc(12px - 3px)' : 1.5,
                                                borderLeft: active ? '3px solid #FFD700' : '3px solid transparent',
                                                color: active ? 'primary.main' : 'text.secondary',
                                                bgcolor: active ? 'rgba(255,215,0,0.08)' : 'transparent',
                                                '&:hover': {
                                                    bgcolor: 'rgba(255,215,0,0.08)',
                                                    color: 'primary.main',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                                                <Icon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.name}
                                                primaryTypographyProps={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: active ? 600 : 400,
                                                    noWrap: true,
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                        {gi < MenuGroups.length - 1 && (
                            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 2, mt: 0.5 }} />
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function MainLayout() {
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const navigate = useNavigate();
    const { mode, toggleTheme } = useThemeMode();

    const handleNavigation = (url: string) => {
        navigate(getBaseURLWithAuth() + url);
        setMobileOpen(false);
    };

    const drawerSx = {
        '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            bgcolor: 'background.paper',
            borderRight: '1px solid rgba(255,215,0,0.08)',
            boxSizing: 'border-box' as const,
        },
    };

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
            {/* Top AppBar */}
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
                    ml: { md: `${DRAWER_WIDTH}px` },
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid rgba(255,215,0,0.08)',
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        onClick={() => setMobileOpen(true)}
                        edge="start"
                        sx={{ mr: 1, display: { md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{
                            display: { xs: 'flex', md: 'none' },
                            flexGrow: 1,
                            color: 'primary.main',
                            letterSpacing: '0.04em',
                            fontSize: '1rem',
                        }}
                    >
                        CryptoPortfolio
                    </Typography>
                    <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }} />
                    <Tooltip title={mode === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}>
                        <IconButton
                            onClick={toggleTheme}
                            size="small"
                            sx={{
                                mr: 1.5,
                                width: 36,
                                height: 36,
                                color: 'text.primary',
                                bgcolor: 'action.hover',
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:hover': { bgcolor: 'action.selected', borderColor: 'primary.main' },
                            }}
                        >
                            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <UserIcon />
                </Toolbar>
            </AppBar>

            {/* Mobile drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{ display: { xs: 'block', md: 'none' }, ...drawerSx }}
            >
                <SidebarContent onNavigate={handleNavigation} />
            </Drawer>

            {/* Desktop permanent drawer */}
            <Drawer
                variant="permanent"
                sx={{ display: { xs: 'none', md: 'block' }, width: DRAWER_WIDTH, flexShrink: 0, ...drawerSx }}
                open
            >
                <SidebarContent onNavigate={handleNavigation} />
            </Drawer>

            {/* Page content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.default',
                }}
            >
                <Toolbar />
                <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 4, md: 5 }, flexGrow: 1 }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
}

export default MainLayout;
